import React, { useEffect, useState } from "react";
import styles from "../styles/DreamPage.module.css";

const IMG = (name) => `${process.env.PUBLIC_URL}/dream-screenshots/${name}`;

function DreamPage() {
  const [phase, setPhase] = useState("intro"); // intro → fadeOut → content

  useEffect(() => {
    const t1 = setTimeout(() => setPhase("fadeOut"), 1800);
    const t2 = setTimeout(() => setPhase("content"), 2800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  if (phase !== "content") {
    return (
      <div className={`${styles.intro} ${phase === "fadeOut" ? styles.introFadeOut : ""}`}>
        <img src={IMG("00_logo.png")} alt="들어드림" className={styles.introLogo} />
        <h1 className={styles.introName}>들어드림</h1>
        <p className={styles.introTagline}>간편한 생활폐기물 수거 서비스</p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* ── Hero ── */}
      <section className={styles.hero}>
        <div className={styles.heroText}>
          <span className={styles.badge}>OVERVIEW</span>
          <h1 className={styles.heroTitle}>
            <span>들어드림</span>을<br />소개합니다
          </h1>
          <p className={styles.heroDesc}>
            <strong>들어드림</strong>은 가구, 가전 등 생활폐기물의 수거를 간편하게
            신청하고 관리할 수 있는 모바일 서비스입니다.
            이용자는 앱에서 <strong>수거 장소와 날짜, 품목을 선택</strong>하기만 하면
            작업자가 직접 방문하여 수거를 진행합니다.
            수거 요청부터 완료까지의 전 과정을 앱에서 확인할 수 있으며,{" "}
            <strong>실시간 알림</strong>으로 진행 상황을 놓치지 않습니다.
          </p>
        </div>
        <div className={styles.heroImage}>
          <img src={IMG("02_user_home.png")} alt="들어드림 홈 화면" className={styles.heroPhone} width="260" height="520" />
        </div>
      </section>

      {/* ── How it Works ── */}
      <section className={styles.stepsSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.badge}>HOW IT WORKS</span>
          <h2 className={styles.sectionTitle}>이렇게 사용해요</h2>
          <p className={styles.sectionSub}>4단계로 간편하게 수거를 신청하세요</p>
        </div>
        <div className={styles.stepsGrid}>
          {[
            { n: "1", t: "수거 장소 등록", img: "01_address_search.png", d: "주소를 검색하고 상세 위치(동/호수 등)를 입력합니다." },
            { n: "2", t: "희망 날짜 선택", img: "05_register_form.png", d: "원하는 수거 날짜를 선택합니다. 예약 불가 날짜는 자동 표시됩니다." },
            { n: "3", t: "품목 선택", img: "06_item_select.png", d: "150개 이상의 품목 중 수거를 원하는 품목과 수량을 선택합니다." },
            { n: "4", t: "작업자 배정 & 수거 완료", img: "07_item_confirm.png", d: "작업자가 배정되면 알림으로 안내되고, 희망일에 방문하여 수거합니다." },
          ].map((s, i) => (
            <div className={styles.stepCard} key={i}>
              <div className={styles.stepNum}>{s.n}</div>
              <h3 className={styles.stepTitle}>{s.t}</h3>
              <img src={IMG(s.img)} alt={s.t} className={styles.stepPhone} loading="lazy" width="180" height="360" />
              <p className={styles.stepDesc}>{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── 소셜 로그인 ── */}
      <section className={styles.featureSec}>
        <div className={styles.sectionHeader}>
          <span className={styles.badge}>FEATURES</span>
          <h2 className={styles.sectionTitle}>주요 기능</h2>
        </div>
        <div className={styles.featureRow}>
          <div className={styles.fText}>
            <div className={styles.fTitle}><span>🔑</span> 간편한 소셜 로그인</div>
            <p className={styles.fDesc}>
              카카오, 네이버 계정으로 간편하게 가입하고 로그인할 수 있습니다.
              별도의 회원가입 절차 없이 기존 소셜 계정을 활용하여 빠르게 시작할 수 있으며,
              가입 시 이용자/작업자 역할을 선택합니다.
            </p>
          </div>
          <div className={styles.fImages}>
            <img src={IMG("03_login.png")} alt="로그인" className={styles.phone} loading="lazy" width="200" height="400" />
            <img src={IMG("04_social_register.png")} alt="소셜 회원가입" className={styles.phone} loading="lazy" width="200" height="400" />
          </div>
        </div>
      </section>

      {/* ── 품목 & 가격 ── */}
      <section className={`${styles.featureSec} ${styles.altBg}`}>
        <div className={`${styles.featureRow} ${styles.reverse}`}>
          <div className={styles.fText}>
            <div className={styles.fTitle}><span>💰</span> 150개 이상의 품목 & 투명한 가격</div>
            <p className={styles.fDesc}>
              가구류, 가전, 생활용품 등 카테고리별로 150개 이상의 품목이 등록되어 있습니다.
              각 품목에는 가격이 함께 표시되어 예상 비용을 확인할 수 있으며,
              선택한 품목의 총 금액도 자동으로 계산됩니다.
            </p>
          </div>
          <div className={styles.fImages}>
            <img src={IMG("08_price_list.png")} alt="가격 일람표" className={styles.phone} loading="lazy" width="200" height="400" />
            <img src={IMG("06_item_select.png")} alt="품목 선택" className={styles.phone} loading="lazy" width="200" height="400" />
          </div>
        </div>
      </section>

      {/* ── 홈 & 기록관리 ── */}
      <section className={styles.featureSec}>
        <div className={styles.featureRow}>
          <div className={styles.fText}>
            <div className={styles.fTitle}><span>🏠</span> 홈 화면에서 한눈에 확인</div>
            <p className={styles.fDesc}>
              홈 화면 상단에는 나의 수거 현황이 요약되어 표시됩니다.
              수거 대기, 예정, 완료 건수를 한눈에 파악할 수 있으며,
              7일 이내에 예정된 가장 가까운 수거 일정이 카드 형태로 표시됩니다.
            </p>
            <div className={styles.statusGrid}>
              <div className={`${styles.statusCard} ${styles.pending}`}>
                <div className={styles.statusName}>수거 대기중</div>
                <div className={styles.statusDesc}>작업자 미배정 요청</div>
              </div>
              <div className={`${styles.statusCard} ${styles.scheduled}`}>
                <div className={styles.statusName}>수거 예정</div>
                <div className={styles.statusDesc}>작업자 배정, 방문 예정</div>
              </div>
              <div className={`${styles.statusCard} ${styles.complete}`}>
                <div className={styles.statusName}>수거 완료</div>
                <div className={styles.statusDesc}>수거 완료 처리된 요청</div>
              </div>
              <div className={`${styles.statusCard} ${styles.overdue}`}>
                <div className={styles.statusName}>기한 경과</div>
                <div className={styles.statusDesc}>희망일 경과, 미완료 요청</div>
              </div>
            </div>
          </div>
          <div className={styles.fImages}>
            <img src={IMG("09_record_pending.png")} alt="대기중" className={styles.phoneSm} loading="lazy" width="180" height="360" />
            <img src={IMG("10_record_overdue_complete.png")} alt="기한경과/완료" className={styles.phoneSm} loading="lazy" width="180" height="360" />
          </div>
        </div>
      </section>

      {/* ── 알림 & 연락처 ── */}
      <section className={`${styles.featureSec} ${styles.altBg}`}>
        <div className={`${styles.featureRow} ${styles.reverse}`}>
          <div className={styles.fText}>
            <div className={styles.fTitle}><span>🔔</span> 실시간 푸시 알림</div>
            <p className={styles.fDesc}>
              수거 요청의 주요 진행 상황을 실시간 푸시 알림으로 전달합니다.
              작업자 배정 시 양쪽 모두에게, 수거 완료 시 이용자에게 알림이 발송됩니다.
            </p>
            <div className={styles.fTitle} style={{ marginTop: 24 }}>
              <span>📞</span> 연락처 공유 & 전화 걸기
            </div>
            <p className={styles.fDesc}>
              작업자가 배정되면 연락처가 자동 공유됩니다.
              수거 상세 화면에서 전화 아이콘을 탭하면 바로 전화를 걸 수 있습니다.
            </p>
            <div className={styles.highlightBox}>
              <h4>✏️ 수거 요청 변경 & 취소</h4>
              <p>작업자 미배정 상태에서는 날짜·품목 변경 및 취소가 가능합니다. 배정 이후에는 버튼이 비활성화되어 혼선을 방지합니다.</p>
            </div>
          </div>
          <div className={styles.fImages}>
            <img src={IMG("11_notification_user.png")} alt="이용자 알림" className={styles.phoneSm} loading="lazy" width="180" height="360" />
            <img src={IMG("13_worker_notification.png")} alt="작업자 알림" className={styles.phoneSm} loading="lazy" width="180" height="360" />
          </div>
        </div>
      </section>

      {/* ── 작업자 화면 ── */}
      <section className={styles.featureSec}>
        <div className={styles.sectionHeader}>
          <span className={styles.badge}>WORKER</span>
          <h2 className={styles.sectionTitle}>작업자 화면</h2>
          <p className={styles.sectionSub}>수거를 담당하는 작업자에게도 전용 화면을 제공합니다</p>
        </div>
        <div className={styles.featureRow}>
          <div className={styles.fText}>
            <div className={styles.fTitle}><span>📱</span> 배정된 작업 확인</div>
            <p className={styles.fDesc}>
              작업자의 홈 화면에는 현재 배정된 수거 현황과 다가오는 작업이 표시됩니다.
              새로운 수거 요청이 배정되면 푸시 알림으로 즉시 안내됩니다.
            </p>
            <div className={styles.fTitle} style={{ marginTop: 24 }}>
              <span>🗺️</span> 경로 안내
            </div>
            <p className={styles.fDesc}>
              "지도에서 보기" 버튼으로 카카오맵, 네이버 지도, Apple 지도 중
              원하는 앱으로 경로 안내를 시작할 수 있습니다.
            </p>
          </div>
          <div className={styles.fImages}>
            <img src={IMG("12_worker_home.png")} alt="작업자 홈" className={styles.phoneSm} loading="lazy" width="180" height="360" />
            <img src={IMG("14_worker_scheduled.png")} alt="작업자 수거 예정" className={styles.phoneSm} loading="lazy" width="180" height="360" />
          </div>
        </div>
      </section>

      {/* ── 수거 완료 ── */}
      <section className={`${styles.featureSec} ${styles.altBg}`}>
        <div className={`${styles.featureRow} ${styles.reverse}`}>
          <div className={styles.fText}>
            <div className={styles.fTitle}><span>✅</span> 수거 완료 처리</div>
            <p className={styles.fDesc}>
              수거 현장에서 요청된 아이템과 수량을 확인한 뒤,
              메모를 남기고 수거 완료 처리를 합니다.
              완료 처리 시 이용자에게 자동으로 알림이 발송됩니다.
            </p>
          </div>
          <div className={styles.fImages}>
            <img src={IMG("17_worker_overdue_complete.png")} alt="작업자 기록" className={styles.phoneSm} loading="lazy" width="180" height="360" />
            <img src={IMG("18_worker_complete_form.png")} alt="수거 완료" className={styles.phoneSm} loading="lazy" width="180" height="360" />
          </div>
        </div>
      </section>

      {/* ── Web Admin ── */}
      <section className={styles.webSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.badge}>WEB ADMIN</span>
          <h2 className={styles.sectionTitle}>관리자 웹 페이지</h2>
          <p className={styles.sectionSub}>별도의 앱 설치 없이 웹 브라우저에서 전체 서비스를 운영합니다</p>
        </div>
        <div className={styles.webGrid}>
          {[
            { img: "20_web_assign_dialog.png", icon: "👥", t: "수거 요청 조회 & 작업자 배정", d: "이용자별 수거 요청 현황을 조회하고, 미배정 요청에 작업자를 즉시 배정합니다. 일괄 배정도 지원합니다." },
            { img: "21_web_calendar.png", icon: "📅", t: "캘린더 뷰", d: "날짜별 수거 현황을 캘린더 형태로 한눈에 확인합니다. 대기, 배정됨, 완료 건수가 색상으로 구분됩니다." },
            { img: "22_web_records_csv.png", icon: "📊", t: "수거 완료 기록 & CSV 내보내기", d: "완료 기록을 날짜, 이용자, 품목, 금액과 함께 조회하고, 엑셀 호환 CSV로 내보낼 수 있습니다." },
            { img: "23_web_stats.png", icon: "📈", t: "통계 & 성과 대시보드", d: "핵심 지표를 카드로 보여주고, 상태별 비율 차트와 월별 추이 그래프로 운영 현황을 파악합니다." },
          ].map((c, i) => (
            <div className={styles.webCard} key={i}>
              <img src={IMG(c.img)} alt={c.t} className={styles.webCardImg} loading="lazy" />
              <div className={styles.webCardBody}>
                <div className={styles.webCardTitle}><span>{c.icon}</span> {c.t}</div>
                <p className={styles.webCardDesc}>{c.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── 운영 도구 ── */}
      <section className={styles.toolsSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.badge}>TOOLS</span>
          <h2 className={styles.sectionTitle}>효율적인 운영 도구</h2>
        </div>
        <div className={styles.toolsGrid}>
          {[
            { e: "📋", t: "일괄 배정", d: "미배정 수거 요청을 선택하여 한 번에 작업자를 배정" },
            { e: "📥", t: "CSV 내보내기", d: "품목, 금액 상세 포함 엑셀 호환 파일로 내보내기" },
            { e: "🚫", t: "예약 불가 날짜 관리", d: "특정 날짜의 수거 예약을 차단하여 일정 조율" },
            { e: "🗑️", t: "수거 요청 삭제", d: "잘못 등록된 요청을 관리자가 직접 삭제 처리" },
          ].map((tool, i) => (
            <div className={styles.toolCard} key={i}>
              <div className={styles.toolEmoji}>{tool.e}</div>
              <div className={styles.toolTitle}>{tool.t}</div>
              <div className={styles.toolDesc}>{tool.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Platform ── */}
      <section className={styles.platformSection}>
        <span className={styles.badge}>PLATFORM</span>
        <h2 className={styles.platformTitle}>iOS & Android 모두 지원</h2>
        <p className={styles.platformDesc}>양 플랫폼에서 동일한 기능과 사용 경험을 제공합니다</p>
      </section>
    </div>
  );
}

export default DreamPage;
